import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';

import styled from '@emotion/styled';

import { Button, Breadcrumb, FormGroup } from '@shared/components/bootstrap';
import { FileFormControl } from '@shared/components/fields/file_form_control';
import { InputFormControl } from '@shared/components/fields/input_form_control';
import { UserRole } from '@shared/types/user_role';

import { Dialog } from '@admin/components/helpers/dialog';
import { Panel } from '@admin/components/helpers/panel';
import { ROLES } from '@admin/config/roles';
import { usePusher } from '@admin/hooks';
import { useTitle } from '@admin/hooks/use_title';
import { client } from '@admin/libraries/apollo';
import { useIngestionUploadSaveMutation, useIngestionUploadQuery, Ingestion__Upload__State } from '@admin/schema';

const ROOT_PATH = '/ingestions';

const Actions = styled.div`
  display: flex;
  margin: -4px;
  justify-content: flex-end;
`;

const Action = styled.div`
  padding: 4px;
`;

const useCombinedQueryMutation = () => {
  const [save, { loading: saving, data: saved }] = useIngestionUploadSaveMutation({ client });
  const uploadID = saved?.save.upload?.id;
  const { data: queried, refetch } = useIngestionUploadQuery({
    client,
    variables: { id: uploadID! },
    skip: !uploadID,
  });

  const upload = queried?.upload ?? saved?.save.upload;

  return {
    save,
    refetch,
    saving,
    upload,
  };
};

export const IngestionUploadForm: React.FC = () => {
  useTitle('Import Ingestions');
  const [file, setFile] = useState<File | undefined>();
  const [description, setDescription] = useState<string>('');
  const [spreadsheetID, setSpreadsheetID] = useState<string | undefined>();
  const { save, saving, upload, refetch } = useCombinedQueryMutation();
  const loading = saving || upload?.state === Ingestion__Upload__State.Pending;

  usePusher<{ id: number }>('ingestion-upload', 'changed', async ({ id }) => {
    if (!upload || String(id) !== upload.id) return;
    refetch();
  });

  if (!ROLES.has(UserRole.Admin)) {
    return null;
  }

  const onUpload = (updatedFile: File, updatedSpreadsheetID: string) => {
    setFile(updatedFile);
    setSpreadsheetID(updatedSpreadsheetID);
  };

  const onSave = (event: React.FormEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (!spreadsheetID) return;
    if (!description) return;

    save({
      variables: {
        input: {
          spreadsheetID,
          description,
        },
      },
    });
  };

  return (
    <>
      {upload && (
        <>
          {upload.state === Ingestion__Upload__State.Processed && (
            <Dialog
              title="Processed"
              description="Your upload processed!"
              onClose={() => (location.href = ROOT_PATH)}
            />
          )}

          {upload.state === Ingestion__Upload__State.Failed && (
            <Dialog
              title="Failed"
              description={upload.error ?? 'Your upload failed!'}
              onClose={() => (location.href = ROOT_PATH)}
            />
          )}
        </>
      )}

      <div className="page-header">
        <Breadcrumb>
          <Breadcrumb.Item>
            <NavLink to={ROOT_PATH}>Ingestion Uploads</NavLink>
          </Breadcrumb.Item>
          <Breadcrumb.Item active>Import</Breadcrumb.Item>
        </Breadcrumb>
      </div>

      <form onSubmit={onSave}>
        <Panel>
          <Panel.Header>
            <Panel.Title>Upload CSV</Panel.Title>
          </Panel.Header>
          <Panel.Body>
            <p>
              Ingestion can be done in bulk by uploading an XLSX. Download and replace the following template:{' '}
              <a href="/files/ingestion.xlsx" className="btn-link" download target="_blank">
                ingestion.xlsx
              </a>
              . On the LOCATION sheet, optional NEW_BARCODE reassigns barcode in place (inventory stays attached). Leave
              it blank to create or update by BARCODE as usual.
            </p>
            <FormGroup>
              <InputFormControl
                placeholder="Description"
                type="text"
                value={description}
                onChange={(event) => {
                  setDescription(event.target.value);
                }}
              />
            </FormGroup>
            <FormGroup>
              <FileFormControl id="ingestionUploadSignedID" accept=".xlsx" label={file?.name} onUpload={onUpload} />
            </FormGroup>
          </Panel.Body>
          <Panel.Footer>
            <Actions>
              <Action>
                <Button type="submit" kind="primary" disabled={!spreadsheetID || !description} loading={loading}>
                  Save
                </Button>
              </Action>
            </Actions>
          </Panel.Footer>
        </Panel>
      </form>
    </>
  );
};
